import { useEffect, useState } from 'react'
import { cn } from './internal/utils'

/** antd default grouping: thousands separator with a dot decimal. */
const formatValue = (value, { precision, groupSeparator = ',', decimalSeparator = '.' }) => {
  if (value == null || value === '') return ''
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return String(value)

  const fixed = precision != null ? numeric.toFixed(precision) : String(numeric)
  const [integerPart, decimalPart] = fixed.split('.')
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
  return decimalPart ? `${grouped}${decimalSeparator}${decimalPart}` : grouped
}

const Statistic = ({
  title,
  value,
  precision,
  prefix,
  suffix,
  valueStyle,
  groupSeparator = ',',
  decimalSeparator = '.',
  formatter,
  loading = false,
  className,
  style,
  ...rest
}) => (
  <div className={cn('ui-statistic', className)} style={style} {...rest}>
    {title ? <div className="ui-statistic-title ui-label mb-1">{title}</div> : null}
    <div
      className="ui-statistic-content ui-numeric flex items-baseline gap-1 text-2xl font-semibold text-[var(--text-dark)]" style={valueStyle}
    >
      {loading ? (
        <span className="h-7 w-24 animate-pulse rounded bg-[var(--gray-300)]" />
      ) : (
        <>
          {prefix ? <span className="ui-statistic-content-prefix text-base">{prefix}</span> : null}
          <span className="ui-statistic-content-value">
            {formatter
              ? formatter(value)
              : formatValue(value, { precision, groupSeparator, decimalSeparator })}
          </span>
          {suffix ? <span className="ui-statistic-content-suffix text-base">{suffix}</span> : null}
        </>
      )}
    </div>
  </div>
)

const DEFAULT_COUNTDOWN_FORMAT = 'HH:mm:ss'

const pad = (input) => String(input).padStart(2, '0')

const formatCountdown = (remaining, format = DEFAULT_COUNTDOWN_FORMAT) => {
  const totalSeconds = Math.max(0, Math.floor(remaining / 1000))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return format
    .replace('DD', pad(days))
    .replace('HH', pad(hours))
    .replace('mm', pad(minutes))
    .replace('ss', pad(seconds))
}

const Countdown = ({ value, format = DEFAULT_COUNTDOWN_FORMAT, onFinish, onChange, ...rest }) => {
  const target = value instanceof Date ? value.getTime() : Number(value)
  const [remaining, setRemaining] = useState(() => Math.max(0, target - Date.now()))

  useEffect(() => {
    const tick = () => {
      const next = Math.max(0, target - Date.now())
      setRemaining(next)
      onChange?.(next)
      if (next === 0) onFinish?.()
    }
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [onChange, onFinish, target])

  return <Statistic {...rest} value={remaining} formatter={() => formatCountdown(remaining, format)} />
}

Statistic.Countdown = Countdown

export default Statistic
